import { useEffect, useMemo, useState } from 'react';
import { Box, FlexBox, Icon, Link, Typography } from '@vp/swan';
import { AGENT_IMAGE_MIME_TYPES } from '../hooks/useClaudeTurn';
import { getHelpRequestDisplayText, isHelpRequestMessage } from '../utils/isHelpRequestMessage';
import {
    parseToWordTokens,
    renderInlineMarkdown,
    stripMarkdown,
} from '../utils/parseInlineMarkdown';
import { AiAvatar } from './AiAvatar';
import { ComponentMessageView } from './ComponentMessageView';
import type { ImageAttachmentMessage, Message, TextMessage } from '../types/chat';
import type { CtaLink } from '../utils/resolveCtaUrl';

// ─── CtaLinkEl ───────────────────────────────────────────────────────────────

function CtaLinkEl({
    link,
    onCtaLinkClick,
}: {
    link: CtaLink;
    onCtaLinkClick?: (url: string, label: string) => void;
}) {
    return (
        <>
            {' '}
            <Link
                href={link.url}
                target="_blank"
                rel="noopener"
                onClick={() => onCtaLinkClick?.(link.url, link.label)}
            >
                {link.label}
            </Link>
        </>
    );
}

// ─── TypewriterText ──────────────────────────────────────────────────────────

function TypewriterText({
    text,
    speed = 30,
    ctaLink,
    onCtaLinkClick,
}: {
    text: string;
    speed?: number;
    ctaLink?: CtaLink;
    onCtaLinkClick?: (url: string, label: string) => void;
}) {
    const tokens = useMemo(() => parseToWordTokens(text), [text]);
    const [visibleCount, setVisibleCount] = useState(0);

    useEffect(() => {
        if (visibleCount >= tokens.length) return;
        const timer = setTimeout(() => setVisibleCount((c) => c + 1), speed);
        return () => clearTimeout(timer);
    }, [visibleCount, tokens.length, speed]);

    useEffect(() => {
        setVisibleCount(0);
    }, [text]);

    return (
        <Typography>
            <span className="sr-only">{stripMarkdown(text)}</span>
            <span aria-hidden="true">
                {tokens.map((token, i) => {
                    if (token.kind === 'newline') {
                        return i < visibleCount ? <br key={i} /> : null;
                    }
                    const addSpace = i < tokens.length - 1 && tokens[i + 1]?.kind !== 'newline';
                    return (
                        <span
                            key={i}
                            className="typewriter-word"
                            style={{
                                opacity: i < visibleCount ? 1 : 0,
                                transition: 'opacity 200ms ease-in',
                            }}
                        >
                            {token.bold ? <strong>{token.text}</strong> : token.text}
                            {addSpace ? ' ' : ''}
                        </span>
                    );
                })}
            </span>
            {ctaLink && visibleCount >= tokens.length && (
                <CtaLinkEl link={ctaLink} onCtaLinkClick={onCtaLinkClick} />
            )}
        </Typography>
    );
}

// ─── Variant subcomponents ───────────────────────────────────────────────────

function resolveDisplayText(message: TextMessage): string {
    if (isHelpRequestMessage(message.content)) return getHelpRequestDisplayText();
    return message.content || '';
}

function TextContent({
    message,
    animate,
    isUser,
    onCtaLinkClick,
}: {
    message: TextMessage;
    animate?: boolean;
    isUser: boolean;
    onCtaLinkClick?: (url: string, label: string) => void;
}) {
    const displayText = resolveDisplayText(message);
    const ctaLink = isUser ? undefined : message.ctaLink;

    if (animate && !isUser)
        return (
            <TypewriterText text={displayText} ctaLink={ctaLink} onCtaLinkClick={onCtaLinkClick} />
        );
    return (
        <Typography style={{ wordBreak: 'break-word' }}>
            {renderInlineMarkdown(displayText)}
            {ctaLink && <CtaLinkEl link={ctaLink} onCtaLinkClick={onCtaLinkClick} />}
        </Typography>
    );
}

function ImageAttachmentContent({ message }: { message: ImageAttachmentMessage }) {
    return (
        <Box>
            <FlexBox
                gap={3}
                style={{
                    flexWrap: 'wrap',
                    marginBottom: message.caption ? 'var(--swan-sem-space-3)' : '0',
                }}
            >
                {message.images.map((att) =>
                    AGENT_IMAGE_MIME_TYPES.has(att.mediaType) ? (
                        <img
                            key={att.uploadId}
                            src={att.previewUrl}
                            alt={att.fileName}
                            style={{
                                maxWidth: '200px',
                                maxHeight: '200px',
                                borderRadius: 'var(--swan-sem-border-radius-standard)',
                                display: 'block',
                            }}
                        />
                    ) : (
                        <Box
                            key={att.uploadId}
                            style={{
                                width: '200px',
                                borderRadius: 'var(--swan-sem-border-radius-standard)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 'var(--swan-sem-space-2)',
                                padding: 'var(--swan-sem-space-4)',
                                background: 'var(--swan-sem-color-background-standard)',
                            }}
                        >
                            <Icon iconType="fileGeneric" size="40p" alt={att.fileName} />
                            <Typography
                                fontSkin="body-small"
                                style={{
                                    wordBreak: 'break-all',
                                    textAlign: 'center',
                                }}
                            >
                                {att.fileName}
                            </Typography>
                        </Box>
                    )
                )}
            </FlexBox>
            {message.caption && <Typography>{message.caption}</Typography>}
        </Box>
    );
}

// ─── Dispatch ────────────────────────────────────────────────────────────────

function MessageContent({
    message,
    animate,
    isUser,
    onCtaLinkClick,
}: {
    message: Message;
    animate?: boolean;
    isUser: boolean;
    onCtaLinkClick?: (url: string, label: string) => void;
}) {
    switch (message.type) {
        case 'text':
            return (
                <TextContent
                    message={message}
                    animate={animate}
                    isUser={isUser}
                    onCtaLinkClick={onCtaLinkClick}
                />
            );
        case 'imageAttachment':
            return <ImageAttachmentContent message={message} />;
        case 'componentMessage':
            return (
                <ComponentMessageView componentType={message.componentType} data={message.data} />
            );
        case 'componentRequest':
            return null; // Rendered in ChatInputArea, not as a bubble
    }
}

// ─── MessageBubble ───────────────────────────────────────────────────────────

/**
 * A componentMessage representing a confirmed live video call needs tighter
 * bubble padding because the inner card supplies its own background and padding.
 */
function isVideoConfirmationMessage(message: Message): boolean {
    return (
        message.type === 'componentMessage' &&
        message.componentType === 'review-method' &&
        (message.data as { requestType?: unknown }).requestType === 'DesignLive'
    );
}

export function MessageBubble({
    message,
    animate,
    onCtaLinkClick,
}: {
    message: Message;
    animate?: boolean;
    onCtaLinkClick?: (url: string, label: string) => void;
}) {
    const isUser = message.role === 'user';
    const isVideoConfirmation = isVideoConfirmationMessage(message);

    return (
        <FlexBox
            role="group"
            aria-label={isUser ? 'You said' : 'AI assistant said'}
            justifyContent={isUser ? 'flex-end' : 'flex-start'}
            gap={isUser ? 0 : 2}
            marginBottom={5}
        >
            {!isUser && <AiAvatar />}
            <Box
                backgroundColor={isUser ? 'accent' : 'standard'}
                paddingY={4}
                paddingX={isVideoConfirmation ? 4 : 5}
                style={{
                    maxWidth: '576px',
                    borderRadius: isVideoConfirmation
                        ? 'var(--swan-sem-border-radius-strong) var(--swan-sem-border-radius-strong) 0 var(--swan-sem-border-radius-strong)'
                        : 'var(--swan-sem-border-radius-strong)',
                }}
            >
                <MessageContent
                    message={message}
                    animate={animate}
                    isUser={isUser}
                    onCtaLinkClick={onCtaLinkClick}
                />
            </Box>
        </FlexBox>
    );
}
